import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import { Professional } from "../../../../services/professionalService";

type AgendaProfessionalFilterDropdownProps = {
  professionals: Professional[];
  selectedProfessionalIds: number[];
  showInactiveOnCalendar: boolean;
  onToggleProfessional: (professionalId: number) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onToggleShowInactive: (value: boolean) => void;
};

const AgendaProfessionalFilterDropdown = ({
  professionals,
  selectedProfessionalIds,
  showInactiveOnCalendar,
  onToggleProfessional,
  onSelectAll,
  onSelectNone,
  onToggleShowInactive,
}: AgendaProfessionalFilterDropdownProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownId = useId();

  const selectedCount = selectedProfessionalIds.length;

  return (
    <Dropdown
      isOpen={open}
      toggle={() => setOpen((current) => !current)}
      direction="down"
      className="agenda-professional-filter"
    >
      <DropdownToggle
        color="success"
        outline
        caret
        size="sm"
        id={dropdownId}
        aria-label={t("studio.agenda.filters.dropdownLabel")}
        className="agenda-professional-filter-toggle bg-white"
      >
        {t("studio.agenda.filters.dropdownTitle", {
          selected: selectedCount,
          total: professionals.length,
        })}
      </DropdownToggle>
      <DropdownMenu
        container="body"
        positionFixed
        className="agenda-professional-filter-menu p-3"
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <span className="fw-medium">{t("studio.agenda.filters.title")}</span>
          <div className="d-flex gap-1">
            <Button color="light" size="sm" onClick={onSelectAll}>
              {t("studio.agenda.filters.all")}
            </Button>
            <Button color="light" size="sm" onClick={onSelectNone}>
              {t("studio.agenda.filters.keepOne")}
            </Button>
          </div>
        </div>

        {professionals.length === 0 ? (
          <p className="text-muted mb-0 small">{t("studio.agenda.filters.emptyProfessionals")}</p>
        ) : (
          <div className="d-flex flex-column gap-2 agenda-professional-filter-list">
            {professionals.map((professional) => (
              <FormGroup check key={professional.id} className="mb-0">
                <Input
                  type="checkbox"
                  id={`agenda-professional-dropdown-${professional.id}`}
                  checked={selectedProfessionalIds.includes(professional.id)}
                  onChange={() => onToggleProfessional(professional.id)}
                  style={{
                    borderColor: professional.color_hex,
                    backgroundColor: selectedProfessionalIds.includes(professional.id)
                      ? professional.color_hex
                      : undefined,
                  }}
                />
                <Label
                  check
                  htmlFor={`agenda-professional-dropdown-${professional.id}`}
                  className="d-flex align-items-center gap-2 ms-1"
                >
                  <span
                    className="rounded-circle flex-shrink-0"
                    style={{
                      width: "0.625rem",
                      height: "0.625rem",
                      backgroundColor: professional.color_hex,
                    }}
                    aria-hidden="true"
                  />
                  {professional.name}
                </Label>
              </FormGroup>
            ))}
          </div>
        )}

        <FormGroup check className="mb-0 mt-3 pt-3 border-top">
          <Input
            type="checkbox"
            id="agenda-show-inactive-dropdown"
            checked={showInactiveOnCalendar}
            onChange={(event) => onToggleShowInactive(event.target.checked)}
          />
          <Label check htmlFor="agenda-show-inactive-dropdown" className="ms-1">
            {t("studio.agenda.calendar.showInactive")}
          </Label>
        </FormGroup>
      </DropdownMenu>
    </Dropdown>
  );
};

export default AgendaProfessionalFilterDropdown;
